import { Card, Chip } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { ModerationAction } from "@/src/components/ModerationAction";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { useAccountActions } from "@/src/pages/AccountDetail/hooks/useAccountActions";
import type { AdminAccountDetail } from "@/src/pages/Accounts/accounts.type";

type AccountActionsProps = {
  account: AdminAccountDetail["account"];
  disabled: AdminAccountDetail["disabled"];
};

/**
 * Ce qu'on peut faire d'un compte, et ce qui lui a déjà été fait.
 *
 * ## Les deux registres sont séparés par le poids, pas par un filet
 *
 * La **désactivation** se défait : bouton ordinaire, confirmation sobre. La
 * **suppression** ne se défait pas : elle est à part, en rouge sans fond,
 * loin du geste courant — visible pour qui la cherche, jamais sur le trajet du
 * pouce. Le rouge plein est gardé pour le bouton de confirmation, seul endroit
 * où le clic engage.
 *
 * ## L'état de la sanction est affiché avant les boutons
 *
 * Un compte déjà désactivé le dit, avec sa date, son motif et **qui** a
 * tranché. Sans cela, un second administrateur ouvrant le dossier ne verrait
 * qu'un bouton « Réactiver » sans savoir de quoi il lèverait la décision.
 */
export function AccountActions({ account, disabled }: AccountActionsProps) {
  const t = useTranslations("moderation");
  const { locale } = useLocale();
  const { setDisabled, isSettingDisabled, remove, isRemoving } =
    useAccountActions(account.id);

  const isDisabled = disabled !== null;

  const since =
    disabled === null
      ? null
      : new Intl.DateTimeFormat(locale, {
          dateStyle: "long",
          timeStyle: "short",
        }).format(new Date(disabled.at));

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-sm font-semibold tracking-wide uppercase">
          {t("actions")}
        </h2>

        <Chip color={isDisabled ? "danger" : "success"} size="sm">
          {t(isDisabled ? "state-disabled" : "state-active")}
        </Chip>
      </div>

      {disabled === null ? (
        <p className="text-sm text-pretty text-muted">{t("active-note")}</p>
      ) : (
        <div className="flex flex-col gap-1 rounded-lg bg-default p-3 text-default-foreground">
          <p className="text-sm">
            {t("disabled-since", { date: since ?? "" })}
          </p>

          {/*
            ⚠️ Le motif est une note **interne** : l'intéressé ne l'a jamais vu
            et ne le verra pas. Le dire ici évite qu'on l'écrive comme un
            message d'excuse à son destinataire.
          */}
          {disabled.reason ? (
            <p className="text-sm text-pretty whitespace-pre-wrap">
              {disabled.reason}
            </p>
          ) : (
            <p className="text-sm text-muted italic">{t("no-reason")}</p>
          )}

          <div className="flex flex-wrap items-center gap-1 text-xs text-muted">
            <span>{t("decided-by")}</span>
            <AccountBadge account={disabled.by} isLinked />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {/*
          La suppression garde sa place à gauche, à l'opposé du geste courant.
          Sous 480 px la barre se replie et la suppression passe au-dessus :
          c'est l'ordre du DOM, pas un `order` CSS — au clavier, la tabulation
          suivrait sinon un chemin que l'écran contredit.
        */}
        <ModerationAction
          confirmBody={t("delete-account-body", { name: account.username })}
          confirmLabel={t("delete-account-confirm")}
          confirmTitle={t("delete-account-title")}
          isDisabled={isSettingDisabled}
          isPending={isRemoving}
          label={t("delete-account")}
          pendingLabel={t("deleting")}
          tone="danger"
          onConfirm={() => remove()}
        />

        <span className="mr-auto" />

        {isDisabled ? (
          <ModerationAction
            confirmBody={t("enable-body", { name: account.username })}
            confirmLabel={t("enable-confirm")}
            confirmTitle={t("enable-title")}
            isDisabled={isRemoving}
            isPending={isSettingDisabled}
            label={t("enable")}
            pendingLabel={t("enabling")}
            onConfirm={() => setDisabled(false)}
          />
        ) : (
          <ModerationAction
            confirmBody={t("disable-body", { name: account.username })}
            confirmLabel={t("disable-confirm")}
            confirmTitle={t("disable-title")}
            isDisabled={isRemoving}
            isPending={isSettingDisabled}
            label={t("disable")}
            pendingLabel={t("disabling")}
            reasonHint={t("reason-hint")}
            reasonLabel={t("reason")}
            tone="danger"
            onConfirm={(reason) => setDisabled(true, reason)}
          />
        )}
      </div>
    </Card>
  );
}
